import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Building2, Store, Users, ShieldCheck, Workflow, LayoutDashboard, ArrowRight, Check,
  FileText, UserPlus, CalendarCheck, FileSignature, Receipt, CreditCard, Lock, Network,
  Eye, BarChart3, Bell, Boxes, FileSearch, Globe, Cpu, Menu, X, Mail, ChevronDown,
  MessageSquare, Calendar, Sparkles, ClipboardList, Database, Cloud, Code2, Server,
  KeyRound, Activity, UserCheck, Briefcase, Layers, Handshake, Timer, BadgeCheck,
  GitBranch, Search, LifeBuoy, AlertTriangle, TrendingUp, MonitorSmartphone,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { SITE, PRODUCTS, GLANCE_METRICS } from "@/config/site";
import { VsMark } from "@/components/Logo";
import { useSEO } from "@/components/Seo";

/* ---------------------------------- Brand ---------------------------------- */
const Logo = ({ light }) => (
  <Link to="/" className="flex items-center gap-2.5" data-testid="brand-logo">
    <VsMark tile={light} />
    <div className="leading-tight">
      <div className={`font-display font-extrabold text-[17px] tracking-tight ${light ? "text-white" : "text-vs-ink"}`}>VS PartnerHub</div>
      <div className={`text-[10px] ${light ? "text-slate-400" : "text-slate-500"}`}>Powered by Vijay Software Solutions</div>
    </div>
  </Link>
);

const ExtLink = ({ href, className, children, testid }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className={className} data-testid={testid}>{children}</a>
);

/* --------------------------------- Header ---------------------------------- */
const NAV_GROUPS = [
  {
    label: "Platform",
    items: [
      { label: "What is PartnerHub", href: "#platform" },
      { label: "Business Workspaces", href: "#workspaces" },
      { label: "How It Works", href: "#how-it-works" },
      { label: "Capabilities", href: "#capabilities" },
      { label: "Business Record 360°", href: "#records" },
      { label: "Real-Time Visibility", href: "#visibility" },
    ],
  },
  {
    label: "Solutions",
    items: [
      { label: "For Clients", href: "#ws-client" },
      { label: "For Vendors", href: "#ws-vendor" },
      { label: "For Staffing Partners", href: "#ws-staffing" },
    ],
  },
  {
    label: "Products",
    items: PRODUCTS.map((p) => ({
      label: p.name, href: p.url, external: !!p.external, comingSoon: !!p.comingSoon, testid: `nav-${p.testid}`,
    })),
  },
  {
    label: "VS-Solutions",
    items: [
      { label: "About", href: "#company" },
      { label: "Founder", href: "#founder" },
      { label: "Technology", href: "#technology" },
      { label: "Security", href: "#security" },
    ],
  },
  {
    label: "Resources",
    items: [
      { label: "Help", href: "/contact", route: true },
      { label: "Contact", href: "/contact", route: true },
    ],
  },
];

function NavItem({ item, onClick, mobile }) {
  const cls = mobile
    ? "flex items-center justify-between py-1.5 text-sm text-slate-600 hover:text-vs-royal"
    : "flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm text-slate-600 hover:text-vs-royal hover:bg-vs-light transition-colors";
  if (item.external && item.href) {
    return (
      <ExtLink href={item.href} className={cls} testid={item.testid}>
        {item.label}<ArrowRight className="h-3.5 w-3.5 -rotate-45" />
      </ExtLink>
    );
  }
  if (item.route) {
    return (
      <Link to={item.href} onClick={onClick} className={cls} data-testid={item.testid}>
        {item.label}
      </Link>
    );
  }
  return (
    <a href={item.href || "#"} onClick={onClick} className={cls} data-testid={item.testid}>
      {item.label}
      {item.comingSoon && <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Soon</span>}
    </a>
  );
}

function Header() {
  const [scrolled, setScrolled] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [expanded, setExpanded] = React.useState(null);
  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled || open ? "bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm" : "bg-transparent"}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Logo />
        <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
          {NAV_GROUPS.map((g) => (
            <div key={g.label} className="relative group">
              <button className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-slate-600 hover:text-vs-royal transition-colors" data-testid={`nav-group-${g.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                {g.label}<ChevronDown className="h-3.5 w-3.5 transition-transform group-hover:rotate-180" />
              </button>
              <div className="absolute left-0 top-full pt-2 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:visible transition-all duration-200">
                <div className="w-60 rounded-xl border border-slate-200 bg-white shadow-xl p-2">
                  {g.items.map((i) => <NavItem key={i.label} item={i} />)}
                </div>
              </div>
            </div>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          <Link to="/login"><Button variant="ghost" className="text-slate-700" data-testid="nav-login-btn">Login</Button></Link>
          <Link to="/register"><Button className="bg-vs-royal hover:bg-vs-blue text-white shadow-sm" data-testid="nav-getstarted-btn">Get Started<ArrowRight className="ml-1.5 h-4 w-4" /></Button></Link>
        </div>
        <button className="lg:hidden p-2 text-slate-700" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden bg-white border-b border-slate-200 px-4 py-3 max-h-[75vh] overflow-y-auto" data-testid="mobile-menu">
          {NAV_GROUPS.map((g) => (
            <div key={g.label} className="border-b border-slate-100 last:border-0">
              <button onClick={() => setExpanded(expanded === g.label ? null : g.label)}
                className="w-full flex items-center justify-between py-3 text-sm font-semibold text-vs-ink" data-testid={`mobile-group-${g.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                {g.label}<ChevronDown className={`h-4 w-4 transition-transform ${expanded === g.label ? "rotate-180" : ""}`} />
              </button>
              {expanded === g.label && (
                <div className="pb-3 pl-3 space-y-0.5">
                  {g.items.map((i) => <NavItem key={i.label} item={i} mobile onClick={() => setOpen(false)} />)}
                </div>
              )}
            </div>
          ))}
          <div className="flex gap-3 pt-3">
            <Link to="/login" className="flex-1"><Button variant="outline" className="w-full" data-testid="mobile-login-btn">Login</Button></Link>
            <Link to="/register" className="flex-1"><Button className="w-full bg-vs-royal text-white" data-testid="mobile-getstarted-btn">Get Started</Button></Link>
          </div>
        </div>
      )}
    </header>
  );
}

/* ------------------------------ Shared pieces ------------------------------ */
function SectionHead({ eyebrow, title, desc, dark, left }) {
  return (
    <div className={left ? "" : "text-center max-w-2xl mx-auto"}>
      <div className={`text-xs uppercase tracking-wider font-semibold ${dark ? "text-blue-300" : "text-vs-royal"}`}>{eyebrow}</div>
      <h2 className={`font-display font-bold text-3xl sm:text-4xl mt-3 tracking-tight ${dark ? "text-white" : "text-vs-ink"}`}>{title}</h2>
      {desc && <p className={`mt-4 text-sm sm:text-base ${dark ? "text-slate-300" : "text-slate-600"} leading-relaxed`}>{desc}</p>}
    </div>
  );
}

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.5, delay }} className={className}>
    {children}
  </motion.div>
);

const StepFlow = ({ steps, dark }) => (
  <div className="flex items-stretch gap-2 overflow-x-auto pb-2 -mx-1 px-1" data-testid="step-flow">
    {steps.map((s, i) => (
      <React.Fragment key={s}>
        <div className={`shrink-0 rounded-lg border px-3 py-2.5 text-center min-w-[104px] ${dark ? "bg-white/5 border-white/15" : "bg-white border-slate-200 shadow-sm"}`}>
          <div className={`text-[10px] font-bold ${dark ? "text-blue-300" : "text-vs-royal"}`}>{String(i + 1).padStart(2, "0")}</div>
          <div className={`text-xs font-semibold mt-0.5 leading-tight ${dark ? "text-white" : "text-vs-ink"}`}>{s}</div>
        </div>
        {i < steps.length - 1 && <div className="flex items-center shrink-0"><ArrowRight className={`h-3.5 w-3.5 ${dark ? "text-white/40" : "text-slate-300"}`} /></div>}
      </React.Fragment>
    ))}
  </div>
);

/* ----------------------------------- Hero ---------------------------------- */
function EcoNode({ icon: Icon, label, className = "" }) {
  return (
    <div className={`flex items-center gap-2.5 rounded-xl bg-white/10 border border-white/20 backdrop-blur px-4 py-3 shadow-lg ${className}`}>
      <div className="h-8 w-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0"><Icon className="h-4 w-4 text-white" /></div>
      <span className="text-xs font-bold tracking-wide text-white uppercase">{label}</span>
    </div>
  );
}

function HeroVisual() {
  return (
    <div className="relative" data-testid="hero-ecosystem">
      <div className="rounded-2xl border border-white/15 bg-white/5 backdrop-blur-sm p-6 sm:p-8">
        <div className="flex justify-center"><EcoNode icon={Building2} label="Clients" /></div>
        <div className="flex flex-col items-center py-1.5">
          <div className="h-6 w-px bg-gradient-to-b from-white/50 to-white/20" />
          <Activity className="h-3.5 w-3.5 text-blue-300" />
          <div className="h-6 w-px bg-gradient-to-b from-white/20 to-white/50" />
        </div>
        <div className="flex justify-center">
          <div className="rounded-xl vs-gradient border border-white/25 px-6 py-4 shadow-2xl text-center">
            <div className="font-display font-extrabold text-white text-lg tracking-tight">VS PARTNERHUB</div>
            <div className="text-[10px] text-blue-200 uppercase tracking-widest mt-0.5">Enterprise Ecosystem</div>
          </div>
        </div>
        <div className="relative py-1.5">
          <div className="absolute left-1/4 top-0 bottom-0 w-px bg-gradient-to-b from-white/50 to-white/20 -rotate-[18deg] origin-top" />
          <div className="absolute right-1/4 top-0 bottom-0 w-px bg-gradient-to-b from-white/50 to-white/20 rotate-[18deg] origin-top" />
          <div className="h-10" />
        </div>
        <div className="flex justify-between gap-3">
          <EcoNode icon={Store} label="Vendors" className="flex-1 justify-center" />
          <EcoNode icon={Users} label="Staffing Partners" className="flex-1 justify-center" />
        </div>
        <div className="mt-6 grid grid-cols-3 gap-2 text-center">
          {["Procurement", "Workforce", "Operations"].map((t) => (
            <div key={t} className="rounded-lg bg-white/5 border border-white/10 py-2 text-[11px] font-medium text-blue-100">{t}</div>
          ))}
        </div>
      </div>
      <div className="absolute -bottom-5 -left-3 sm:-left-6 bg-white rounded-xl shadow-xl border border-slate-100 p-3.5 hidden sm:flex items-center gap-3">
        <div className="h-9 w-9 rounded-lg bg-emerald-50 flex items-center justify-center"><ShieldCheck className="h-5 w-5 text-emerald-600" /></div>
        <div><div className="text-sm font-semibold text-vs-ink">Verified Network</div><div className="text-xs text-slate-500">Tenant-isolated &amp; audited</div></div>
      </div>
      <div className="absolute -top-4 -right-3 sm:-right-5 bg-white rounded-xl shadow-xl border border-slate-100 p-3.5 hidden sm:flex items-center gap-3">
        <div className="h-9 w-9 rounded-lg bg-vs-light flex items-center justify-center"><Sparkles className="h-5 w-5 text-vs-royal" /></div>
        <div><div className="text-sm font-semibold text-vs-ink">VS AI Built-in</div><div className="text-xs text-slate-500">Smart matching &amp; insights</div></div>
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden vs-gradient pt-24 pb-14 sm:pt-32 sm:pb-20" data-testid="hero-section">
      <div className="absolute inset-0 grid-pattern opacity-40" />
      <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-blue-500/20 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-4 py-1.5 text-xs font-semibold tracking-widest text-blue-100 mb-6">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse-dot" />VS PARTNERHUB
          </div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.08] tracking-tight text-balance">
            Powering Smarter Enterprise Partnerships
          </h1>
          <p className="mt-5 text-base sm:text-lg text-blue-100/90 max-w-xl leading-relaxed">
            A unified enterprise platform connecting clients, vendors and staffing partners across procurement, workforce and business operations.
          </p>
          <div className="mt-7 flex flex-wrap gap-3.5">
            <Link to="/register"><Button size="lg" className="bg-white text-vs-deep hover:bg-blue-50 font-semibold shadow-lg" data-testid="hero-getstarted-btn">Get Started<ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
            <a href="#platform"><Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 bg-transparent" data-testid="hero-explore-btn">Explore the Platform<ArrowRight className="ml-2 h-4 w-4" /></Button></a>
          </div>
          <div className="mt-6 text-xs text-blue-200/80 font-medium tracking-wide">{SITE.tagline}</div>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }}>
          <HeroVisual />
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------------------- Platform at a Glance ---------------------------- */
function AtAGlance() {
  return (
    <section className="py-12 sm:py-16 bg-white border-b border-slate-100" data-testid="at-a-glance-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead eyebrow="Platform at a Glance" title="One connected ecosystem" desc="Designed to manage the complete business partnership lifecycle — capability highlights, not customer counts." />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-8 sm:mt-10">
          {GLANCE_METRICS.map((m, i) => (
            <Reveal key={m.label} delay={i * 0.04}>
              <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5 h-full hover:border-vs-royal transition-colors" data-testid={`glance-metric-${i}`}>
                <div className="font-display font-extrabold text-2xl sm:text-3xl text-vs-royal tracking-tight">{m.value}</div>
                <div className="text-sm font-bold text-vs-ink mt-1">{m.label}</div>
                <div className="text-xs text-slate-500 mt-1 leading-relaxed">{m.desc}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------------------- What is PartnerHub ---------------------------- */
const PLATFORM_CAPABILITIES_LIST = [
  { icon: ClipboardList, t: "Enterprise Requirements" }, { icon: Handshake, t: "Procurement" },
  { icon: Store, t: "Vendor Management" }, { icon: Users, t: "Staffing & Workforce Operations" },
  { icon: UserPlus, t: "Candidate Submissions" }, { icon: CalendarCheck, t: "Interviews" },
  { icon: BadgeCheck, t: "Selection & Onboarding" }, { icon: FileSearch, t: "RFQs & Quotations" },
  { icon: FileText, t: "Purchase Orders" }, { icon: FileSignature, t: "Contracts" },
  { icon: Receipt, t: "Invoices & Payments" }, { icon: Lock, t: "Documents & Verification" },
  { icon: MessageSquare, t: "Communication" }, { icon: BarChart3, t: "Analytics" },
  { icon: Sparkles, t: "AI-Assisted Workflows" },
];

function WhatIs() {
  return (
    <section id="platform" className="py-14 sm:py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="what-is-section">
      <div className="grid lg:grid-cols-5 gap-10 items-start">
        <div className="lg:col-span-2 lg:sticky lg:top-24">
          <SectionHead left eyebrow="The Platform" title="What is VS PartnerHub?" />
          <p className="mt-4 text-sm sm:text-base text-slate-600 leading-relaxed">
            VS PartnerHub is an enterprise collaboration and business operations platform that connects clients, vendors and staffing partners through one secure digital ecosystem.
          </p>
          <p className="mt-3 text-sm sm:text-base text-slate-600 leading-relaxed">
            Instead of scattered emails, spreadsheets and disconnected tools, every requirement, quotation, candidate, contract, invoice and payment flows through a single governed platform — with full visibility for every participant.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {["Procurement", "Workforce", "Vendor Operations", "Finance", "Intelligence"].map((t) => (
              <span key={t} className="rounded-full bg-vs-light border border-blue-100 px-3 py-1 text-xs font-semibold text-vs-royal">{t}</span>
            ))}
          </div>
        </div>
        <div className="lg:col-span-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
          {PLATFORM_CAPABILITIES_LIST.map((c, i) => (
            <Reveal key={c.t} delay={i * 0.03}>
              <div className="vs-card vs-card-hover p-4 h-full flex flex-col gap-2.5 group" data-testid={`capability-tile-${i}`}>
                <div className="h-9 w-9 rounded-lg bg-vs-light flex items-center justify-center group-hover:bg-vs-royal transition-colors">
                  <c.icon className="h-4.5 w-4.5 h-[18px] w-[18px] text-vs-royal group-hover:text-white transition-colors" />
                </div>
                <div className="text-sm font-semibold text-vs-ink leading-snug">{c.t}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------ Four Workspaces ----------------------------- */
const WORKSPACES = [
  {
    id: "ws-client", num: "01", tag: "Client", title: "Client Workspace", icon: Building2,
    desc: "Create business requirements, engage vendors and staffing partners, evaluate responses and manage the complete business lifecycle.",
    caps: ["Create Requirements", "Vendor Discovery", "Staffing Partners", "Candidate Evaluation", "Selection", "Purchase Orders", "Contracts", "Invoices", "Payments"],
    cta: { label: "Explore Client Workspace", href: "/register?role=client" }, accent: "bg-vs-royal", testid: "workspace-client",
  },
  {
    id: "ws-vendor", num: "02", tag: "Vendor", title: "Vendor Workspace", icon: Store,
    desc: "Manage business opportunities, respond to RFQs, submit quotations and manage orders, contracts and payments.",
    caps: ["Company Profile", "Capabilities", "RFQ Invitations", "Quotations", "Purchase Orders", "Contracts", "Invoices", "Payments", "Documents", "Performance Analytics"],
    cta: { label: "Explore Vendor Workspace", href: "/register?role=vendor" }, accent: "bg-emerald-600", testid: "workspace-vendor",
  },
  {
    id: "ws-staffing", num: "03", tag: "Staffing Partner", title: "Staffing Workspace", icon: Users,
    desc: "Manage workforce requirements from sourcing and candidate submission through interviews, selection, joining and deployment.",
    pipeline: ["Requirement", "Source", "Screen", "Submit", "Shortlist", "Interview", "Select", "Offer", "BGV", "Join", "Deploy"],
    caps: ["Opportunity Board", "Requirements", "Candidate Database", "Candidate Submission", "Interviews", "Selection", "Offers", "BGV", "Onboarding", "Deployment", "Replacement"],
    cta: { label: "Explore Staffing Workspace", href: "/register?role=staffing" }, accent: "bg-amber-600", testid: "workspace-staffing",
  },
];

function Workspaces() {
  return (
    <section id="workspaces" className="py-14 sm:py-20 bg-slate-50 border-y border-slate-100" data-testid="workspaces-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead eyebrow="Workspaces" title="One Platform. Three Powerful Workspaces." desc="One connected platform with dedicated workspaces for clients, vendors and staffing partners." />
        <div className="grid md:grid-cols-3 gap-5 sm:gap-6 mt-10 sm:mt-12">
          {WORKSPACES.map((w, i) => (
            <Reveal key={w.id} delay={i * 0.06} className="min-w-0">
              <div id={w.id} className="vs-card vs-card-hover overflow-hidden h-full flex flex-col scroll-mt-24 min-w-0" data-testid={w.testid}>
                <div className="p-5 sm:p-6 border-b border-slate-100 flex items-start gap-4">
                  <div className={`h-11 w-11 rounded-xl ${w.accent} flex items-center justify-center shrink-0 shadow-sm`}>
                    <w.icon className="h-5 w-5 text-white" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-bold text-slate-400">{w.num}</span>
                      <span className="text-[10px] font-bold uppercase tracking-widest text-vs-royal">{w.tag}</span>
                    </div>
                    <h3 className="font-display font-bold text-lg text-vs-ink mt-0.5">{w.title}</h3>
                    <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{w.desc}</p>
                  </div>
                </div>
                {w.pipeline && (
                  <div className="px-5 sm:px-6 pt-4">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Hiring Pipeline</div>
                    <div className="flex items-center gap-1 overflow-x-auto pb-1">
                      {w.pipeline.map((p, pi) => (
                        <React.Fragment key={p}>
                          <span className="shrink-0 rounded-md bg-vs-light border border-blue-100 px-2 py-1 text-[10px] font-semibold text-vs-royal">{p}</span>
                          {pi < w.pipeline.length - 1 && <ArrowRight className="h-3 w-3 text-slate-300 shrink-0" />}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                )}
                <div className="p-5 sm:p-6 flex-1">
                  <div className="flex flex-wrap gap-1.5">
                    {w.caps.map((c) => (
                      <span key={c} className="rounded-md bg-slate-50 border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700 hover:border-vs-royal hover:text-vs-royal transition-colors">{c}</span>
                    ))}
                  </div>
                </div>
                <div className="px-5 sm:px-6 pb-5">
                  <Link to={w.cta.href} className="inline-flex items-center gap-1.5 text-sm font-semibold text-vs-royal hover:gap-2.5 transition-all" data-testid={`${w.testid}-cta`}>
                    {w.cta.label}<ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ----------------------------- How PartnerHub Works ---------------------------- */
const WORKFLOW_TABS = {
  Client: ["Create Requirement", "Invite Partners", "Receive Responses", "Evaluate", "Select", "Contract / PO", "Delivery", "Invoice", "Payment"],
  Vendor: ["Receive RFQ", "Review", "Clarify", "Submit Quotation", "Evaluation", "Award", "PO", "Delivery", "Invoice", "Payment"],
  Staffing: ["Receive Requirement", "Source Talent", "Screen", "Submit Candidate", "Interview", "Selection", "Offer", "BGV", "Joining", "Deployment"],
};

function HowItWorks() {
  const [tab, setTab] = React.useState("Client");
  return (
    <section id="how-it-works" className="py-14 sm:py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="how-it-works-section">
      <SectionHead eyebrow="Workflows" title="From Requirement to Business Outcome" desc="Three connected business flows run through the same governed platform — every step tracked, every action auditable." />
      <div className="flex justify-center mt-8">
        <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1 shadow-sm" data-testid="workflow-tabs">
          {Object.keys(WORKFLOW_TABS).map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-4 sm:px-6 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === t ? "bg-vs-royal text-white shadow-sm" : "text-slate-600 hover:text-vs-ink"}`}
              data-testid={`workflow-tab-${t.toLowerCase()}`}>
              {t}
            </button>
          ))}
        </div>
      </div>
      <motion.div key={tab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className="mt-8">
        <div className="vs-card p-5 sm:p-8">
          <div className="flex items-center gap-2 mb-5">
            {tab === "Client" && <Building2 className="h-4 w-4 text-vs-royal" />}
            {tab === "Vendor" && <Store className="h-4 w-4 text-emerald-600" />}
            {tab === "Staffing" && <Users className="h-4 w-4 text-amber-600" />}
            <span className="text-xs font-bold uppercase tracking-widest text-slate-500">{tab} Workflow</span>
          </div>
          <StepFlow steps={WORKFLOW_TABS[tab]} />
        </div>
      </motion.div>
    </section>
  );
}

/* ----------------------------- Platform Capabilities ----------------------------- */
const CAPABILITY_GROUPS = [
  { icon: Briefcase, title: "Business Operations", color: "text-vs-royal bg-vs-light", items: [
    { t: "Requirements Management", d: "Structured intake of service & staffing needs." },
    { t: "Partner Management", d: "Onboard, verify and manage every partner." },
    { t: "Approval Workflows", d: "Governed multi-step approvals." },
    { t: "SLA Management", d: "Track commitments and escalations." },
  ]},
  { icon: Handshake, title: "Procurement", color: "text-emerald-600 bg-emerald-50", items: [
    { t: "RFQ / RFP", d: "Invite partners to bid on requirements." },
    { t: "Quotations", d: "Receive structured commercial proposals." },
    { t: "Comparison", d: "Side-by-side quotation evaluation." },
    { t: "Purchase Orders", d: "Convert awards into formal orders." },
    { t: "Contracts", d: "Digitally managed agreements." },
  ]},
  { icon: Users, title: "Workforce", color: "text-amber-600 bg-amber-50", items: [
    { t: "Staffing Requirements", d: "Role-wise hiring demand with skills & rates." },
    { t: "Candidate Database", d: "Searchable talent pools per partner." },
    { t: "Candidate Submissions", d: "Profile submissions against requirements." },
    { t: "Interviews", d: "Scheduling, feedback and outcomes." },
    { t: "Selection", d: "Offers and client decisions." },
    { t: "BGV", d: "Background verification tracking." },
    { t: "Onboarding", d: "Joining formalities and documentation." },
    { t: "Deployment", d: "Active deployment records." },
    { t: "Replacements", d: "Managed backfills and exits." },
  ]},
  { icon: Receipt, title: "Finance", color: "text-rose-600 bg-rose-50", items: [
    { t: "Invoices", d: "Partner and client invoicing." },
    { t: "Payment Tracking", d: "Status from raised to settled." },
    { t: "Outstanding Payments", d: "Aging and follow-up visibility." },
    { t: "Financial Workflows", d: "Approval-linked money movement." },
  ]},
  { icon: MessageSquare, title: "Collaboration", color: "text-violet-600 bg-violet-50", items: [
    { t: "Messages", d: "In-platform business communication." },
    { t: "Notifications", d: "Event-driven alerts for every role." },
    { t: "Email", d: "Branded transactional email." },
    { t: "Activity Timeline", d: "Complete record-level history." },
    { t: "Business Calendar", d: "Interviews, deadlines and renewals." },
  ]},
  { icon: Sparkles, title: "Intelligence", color: "text-blue-600 bg-blue-50", items: [
    { t: "Analytics", d: "Cross-module operational dashboards." },
    { t: "Smart Matching", d: "Requirement-to-partner matching." },
    { t: "VS AI", d: "Role-aware AI assistant." },
    { t: "Executive Insights", d: "Decision-ready summaries." },
  ]},
];

function Capabilities() {
  return (
    <section id="capabilities" className="py-14 sm:py-20 bg-slate-50 border-y border-slate-100" data-testid="capabilities-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead eyebrow="Capabilities" title="Everything Needed to Run Connected Business Operations" desc="Six deeply integrated capability areas — one data model, one audit trail, one platform." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10 sm:mt-12">
          {CAPABILITY_GROUPS.map((g, i) => (
            <Reveal key={g.title} delay={i * 0.05}>
              <div className="vs-card vs-card-hover p-5 h-full" data-testid={`capability-group-${i}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className={`h-9 w-9 rounded-lg ${g.color} flex items-center justify-center`}><g.icon className="h-4.5 w-4.5 h-[18px] w-[18px]" /></div>
                  <h3 className="font-display font-bold text-base text-vs-ink uppercase tracking-wide">{g.title}</h3>
                </div>
                <ul className="space-y-2.5">
                  {g.items.map((it) => (
                    <li key={it.t} className="group">
                      <div className="flex items-center gap-2 text-sm font-semibold text-slate-800 group-hover:text-vs-royal transition-colors">
                        <Check className="h-3.5 w-3.5 text-vs-royal shrink-0" />{it.t}
                      </div>
                      <div className="text-xs text-slate-500 pl-5.5 pl-[22px] leading-relaxed">{it.d}</div>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------ Business Record 360 ------------------------------ */
const RECORDS_360 = {
  "Client 360°": ["Profile", "Requirements", "RFQs", "Contracts", "Invoices", "Payments", "Activity"],
  "Vendor 360°": ["Company", "Capabilities", "RFQs", "Quotations", "POs", "Contracts", "Invoices", "Payments", "Performance"],
  "Requirement 360°": ["Requirement", "Partners", "Candidates", "Interviews", "Selection", "Contract", "Deployment", "Analytics"],
  "RFQ 360°": ["RFQ", "Invitations", "Quotations", "Comparison", "Award", "PO", "Contract", "Invoices"],
  "Candidate 360°": ["Profile", "Skills", "Submissions", "Interviews", "Selection", "BGV", "Onboarding", "Deployment", "Activity"],
  "Contract 360°": ["Contract", "Terms", "Milestones", "Invoices", "Payments", "Renewals", "Documents"],
};

function Records360() {
  const keys = Object.keys(RECORDS_360);
  const [rec, setRec] = React.useState("Candidate 360°");
  return (
    <section id="records" className="py-14 sm:py-20 bg-vs-deep relative overflow-hidden" data-testid="records-section">
      <div className="absolute inset-0 grid-pattern opacity-20" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead dark eyebrow="Record Intelligence" title="Complete Visibility Across Every Business Record" desc="Every record carries its full lifecycle — linked, queryable and auditable from a single view." />
        <div className="flex flex-wrap justify-center gap-2 mt-8" data-testid="record-360-tabs">
          {keys.map((k) => (
            <button key={k} onClick={() => setRec(k)}
              className={`rounded-full px-4 py-2 text-xs sm:text-sm font-semibold border transition-colors ${rec === k ? "bg-white text-vs-deep border-white" : "text-blue-100 border-white/25 hover:bg-white/10"}`}
              data-testid={`record-tab-${k.toLowerCase().replace(/[^a-z]/g, "-")}`}>
              {k}
            </button>
          ))}
        </div>
        <motion.div key={rec} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className="mt-8">
          <div className="rounded-2xl border border-white/15 bg-white/5 backdrop-blur-sm p-5 sm:p-8">
            <div className="flex items-center gap-2 mb-5">
              <GitBranch className="h-4 w-4 text-blue-300" />
              <span className="text-xs font-bold uppercase tracking-widest text-blue-200">{rec} — Connected Lifecycle</span>
            </div>
            <StepFlow steps={RECORDS_360[rec]} dark />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* --------------------------- Real-Time Business Visibility --------------------------- */
const BARS_A = [42, 58, 50, 72, 64, 84, 78, 92];
const BARS_B = [80, 64, 70, 52, 60, 44, 38, 30];

function MiniBars({ data, color, label }) {
  return (
    <div>
      <div className="flex items-end gap-1.5 h-24">
        {data.map((h, i) => (
          <div key={i} className={`flex-1 rounded-t ${color}`} style={{ height: `${h}%` }} />
        ))}
      </div>
      <div className="text-[11px] font-semibold text-slate-500 mt-2">{label}</div>
    </div>
  );
}

function Visibility() {
  const kpis = [
    { icon: ClipboardList, label: "Open Requirements", tone: "text-vs-royal bg-vs-light" },
    { icon: FileSearch, label: "Active RFQs", tone: "text-emerald-600 bg-emerald-50" },
    { icon: Users, label: "Candidate Pipeline", tone: "text-amber-600 bg-amber-50" },
    { icon: Receipt, label: "Pending Invoices", tone: "text-rose-600 bg-rose-50" },
    { icon: AlertTriangle, label: "SLA Alerts", tone: "text-orange-600 bg-orange-50" },
  ];
  const activity = [
    { icon: CalendarCheck, t: "Interview scheduled", s: "Candidate pipeline update" },
    { icon: FileSignature, t: "Contract awaiting signature", s: "Procurement workflow" },
    { icon: Store, t: "Vendor quotation received", s: "RFQ response" },
    { icon: CreditCard, t: "Payment marked settled", s: "Finance workflow" },
  ];
  return (
    <section id="visibility" className="py-14 sm:py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="visibility-section">
      <SectionHead eyebrow="Live Operations" title="See Your Business Clearly" desc="Requirements, RFQs, candidates, interviews, contracts, invoices, payments and SLA alerts — monitored in real time across every workspace." />
      <Reveal className="mt-10 sm:mt-12">
        <div className="rounded-2xl border border-slate-200 bg-white shadow-2xl overflow-hidden" data-testid="dashboard-preview">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 bg-slate-50">
            <span className="h-2.5 w-2.5 rounded-full bg-rose-400" /><span className="h-2.5 w-2.5 rounded-full bg-amber-400" /><span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
            <span className="ml-3 text-xs font-semibold text-slate-500">VS PartnerHub — Command View</span>
            <span className="ml-auto rounded-full bg-vs-light border border-blue-100 px-2.5 py-0.5 text-[10px] font-bold text-vs-royal uppercase tracking-wide">Illustrative Preview</span>
          </div>
          <div className="grid lg:grid-cols-[200px_1fr]">
            <div className="hidden lg:block border-r border-slate-100 bg-slate-50/60 p-4 space-y-1">
              {["Overview", "Requirements", "RFQs", "Candidates", "Interviews", "Contracts", "Invoices", "Payments", "Analytics"].map((m, i) => (
                <div key={m} className={`rounded-lg px-3 py-2 text-xs font-semibold ${i === 0 ? "bg-vs-royal text-white" : "text-slate-500"}`}>{m}</div>
              ))}
            </div>
            <div className="p-4 sm:p-6 space-y-5">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {kpis.map((k) => (
                  <div key={k.label} className="rounded-xl border border-slate-200 p-3.5 hover:border-vs-royal transition-colors">
                    <div className={`h-8 w-8 rounded-lg ${k.tone} flex items-center justify-center mb-2`}><k.icon className="h-4 w-4" /></div>
                    <div className="text-xs font-semibold text-slate-700 leading-tight">{k.label}</div>
                    <div className="mt-2 h-1.5 rounded-full bg-slate-100"><div className={`h-full rounded-full ${k.tone.split(" ")[0].replace("text-", "bg-")}`} style={{ width: "62%" }} /></div>
                  </div>
                ))}
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="flex items-center gap-2 mb-3"><TrendingUp className="h-3.5 w-3.5 text-vs-royal" /><span className="text-xs font-bold text-slate-700 uppercase tracking-wide">Requirement Trend</span></div>
                  <MiniBars data={BARS_A} color="bg-vs-royal/80" label="Intake vs closure across periods" />
                </div>
                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="flex items-center gap-2 mb-3"><Users className="h-3.5 w-3.5 text-amber-600" /><span className="text-xs font-bold text-slate-700 uppercase tracking-wide">Staffing Pipeline</span></div>
                  <MiniBars data={BARS_B} color="bg-amber-500/80" label="Submission → deployment conversion" />
                </div>
              </div>
              <div className="rounded-xl border border-slate-200 p-4">
                <div className="flex items-center gap-2 mb-3"><Activity className="h-3.5 w-3.5 text-emerald-600" /><span className="text-xs font-bold text-slate-700 uppercase tracking-wide">Partner Activity</span></div>
                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                  {activity.map((a) => (
                    <div key={a.t} className="flex items-center gap-2.5">
                      <div className="h-7 w-7 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center shrink-0"><a.icon className="h-3.5 w-3.5 text-slate-500" /></div>
                      <div className="min-w-0"><div className="text-xs font-semibold text-slate-800 truncate">{a.t}</div><div className="text-[11px] text-slate-400 truncate">{a.s}</div></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        <p className="text-center text-xs text-slate-400 mt-3">Illustrative interface preview — not live business metrics.</p>
      </Reveal>
    </section>
  );
}

/* --------------------------------- Why PartnerHub --------------------------------- */
const WHY = [
  { icon: Lock, t: "Secure by Design", d: "Role-based access, tenant isolation, authentication, audit trails and controlled business data." },
  { icon: Network, t: "One Connected Ecosystem", d: "Clients, vendors and staffing partners work through one integrated platform." },
  { icon: Workflow, t: "End-to-End Workflows", d: "From requirement creation to delivery, hiring, contracts, invoices and payments." },
  { icon: Eye, t: "Real-Time Visibility", d: "Track requirements, RFQs, candidates, interviews, contracts, invoices and operational activity." },
  { icon: Sparkles, t: "Intelligent Operations", d: "VS AI and smart matching help users understand information and work faster." },
  { icon: Boxes, t: "Enterprise Scalability", d: "Designed for organizations, teams, partners, workflows and large volumes of business data." },
];

function Why() {
  return (
    <section id="why" className="py-14 sm:py-20 bg-slate-50 border-y border-slate-100" data-testid="why-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead eyebrow="Why VS PartnerHub" title="Why VS PartnerHub?" desc="A connected digital infrastructure for modern enterprise partnerships." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10 sm:mt-12">
          {WHY.map((w, i) => (
            <Reveal key={w.t} delay={i * 0.05}>
              <div className="vs-card vs-card-hover p-6 h-full group" data-testid={`why-card-${i}`}>
                <div className="h-11 w-11 rounded-xl bg-vs-light border border-blue-100 flex items-center justify-center mb-4 group-hover:bg-vs-royal transition-colors">
                  <w.icon className="h-5 w-5 text-vs-royal group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-display font-bold text-lg text-vs-ink">{w.t}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{w.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ----------------------------------- Products ----------------------------------- */
function Products() {
  return (
    <section id="products" className="py-14 sm:py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="products-section">
      <SectionHead eyebrow="Product Ecosystem" title="Built by Vijay Software Solutions" desc="A growing family of technology products across enterprise, food-tech, relationships and safety domains." />
      <div className="grid sm:grid-cols-2 gap-5 mt-10 sm:mt-12 max-w-4xl mx-auto">
        {PRODUCTS.map((p, i) => {
          const inner = (
            <>
              <div className="flex items-center gap-4">
                <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${p.gradient} flex items-center justify-center text-white font-display font-extrabold text-xl shadow-sm shrink-0`}>{p.letter}</div>
                <div>
                  <h3 className="font-display font-bold text-lg text-vs-ink">{p.name}</h3>
                  <div className="text-[10px] uppercase tracking-widest text-vs-royal font-bold mt-0.5">{p.category}</div>
                </div>
              </div>
              <p className="text-sm text-slate-600 mt-3 leading-relaxed">{p.desc}</p>
              <div className="mt-4">
                {p.url ? (
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-vs-royal group-hover:gap-2.5 transition-all">
                    Explore {p.name}<ArrowRight className="h-4 w-4" />
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-400"><Timer className="h-4 w-4" />Coming Soon</span>
                )}
              </div>
            </>
          );
          const cls = "vs-card vs-card-hover p-5 sm:p-6 h-full group block";
          return (
            <Reveal key={p.name} delay={i * 0.05}>
              {p.external && p.url ? (
                <ExtLink href={p.url} className={cls} testid={p.testid}>{inner}</ExtLink>
              ) : p.url ? (
                <a href={p.url} className={cls} data-testid={p.testid}>{inner}</a>
              ) : (
                <div className={cls} data-testid={p.testid}>{inner}</div>
              )}
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}

/* ----------------------------------- Company ----------------------------------- */
const COMPANY_SERVICES = [
  "Enterprise Software", "SaaS Platforms", "AI & Machine Learning", "Data Analytics", "Cloud Applications",
  "Mobile Applications", "Web Platforms", "Business Automation", "Digital Products", "Technology Solutions",
];

function Company() {
  return (
    <section id="company" className="py-14 sm:py-20 bg-slate-50 border-y border-slate-100" data-testid="company-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <SectionHead left eyebrow="The Company" title="Vijay Software Solutions Pvt Ltd" desc="Technology. Products. Platforms. Digital Transformation." />
          <p className="mt-4 text-sm sm:text-base text-slate-600 leading-relaxed">
            Vijay Software Solutions builds technology products, enterprise platforms and digital solutions across multiple business domains — from enterprise operations software to consumer-facing digital products.
          </p>
          <div className="mt-6">
            <ExtLink href={SITE.website} testid="company-cta">
              <Button className="bg-vs-royal hover:bg-vs-blue text-white">Explore VS-Solutions<ArrowRight className="ml-2 h-4 w-4" /></Button>
            </ExtLink>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {COMPANY_SERVICES.map((s) => (
            <div key={s} className="vs-card vs-card-hover p-3.5 flex items-center gap-2.5" data-testid={`company-service-${s.toLowerCase().replace(/[^a-z]/g, "-")}`}>
              <div className="h-7 w-7 rounded-lg bg-vs-light flex items-center justify-center shrink-0"><Check className="h-3.5 w-3.5 text-vs-royal" /></div>
              <span className="text-xs sm:text-sm font-semibold text-slate-800">{s}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ----------------------------------- Founder ----------------------------------- */
function Founder() {
  const [imgOk, setImgOk] = React.useState(true);
  return (
    <section id="founder" className="py-14 sm:py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="founder-section">
      <div className="grid lg:grid-cols-5 gap-10 items-center">
        <div className="lg:col-span-2">
          <div className="relative rounded-2xl vs-gradient p-6 sm:p-8 overflow-hidden">
            <div className="absolute inset-0 grid-pattern opacity-20" />
            <div className="relative mx-auto w-full max-w-[280px] aspect-[4/5] rounded-xl overflow-hidden border-2 border-white/25 shadow-2xl bg-vs-navy">
              {imgOk ? (
                <img src={SITE.founderProfileImage} alt="Vijay Bhargav — Founder & CEO, Vijay Software Solutions Pvt Ltd"
                  onError={() => setImgOk(false)} className="h-full w-full object-cover object-top" data-testid="founder-image" />
              ) : (
                <div className="h-full w-full flex flex-col items-center justify-center" data-testid="founder-image-placeholder">
                  <span className="font-display font-extrabold text-5xl text-white/90">VB</span>
                  <span className="text-[10px] uppercase tracking-widest text-blue-200 mt-2">Photograph coming soon</span>
                </div>
              )}
            </div>
            <div className="relative mt-5 text-center">
              <div className="font-display font-bold text-2xl text-white">Vijay Bhargav</div>
              <div className="text-blue-200 text-sm mt-1">Founder &amp; CEO</div>
              <div className="text-blue-100/70 text-xs mt-0.5">Vijay Software Solutions Pvt Ltd</div>
            </div>
          </div>
        </div>
        <div className="lg:col-span-3">
          <div className="text-xs uppercase tracking-wider font-semibold text-vs-royal">Meet the Founder</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-vs-ink mt-3 tracking-tight">Building technology that connects businesses and people</h2>
          <p className="mt-5 text-sm sm:text-base text-slate-600 leading-relaxed">
            Vijay Bhargav is the Founder &amp; CEO of Vijay Software Solutions Pvt Ltd, focused on building technology products, enterprise platforms and digital solutions across multiple business domains.
          </p>
          <blockquote className="mt-5 border-l-2 border-vs-royal pl-4 text-slate-700 italic text-sm sm:text-base">
            "Building practical technology platforms that connect businesses, people and opportunities."
          </blockquote>
          <div className="mt-6 flex flex-wrap gap-3">
            <ExtLink href={SITE.website} testid="founder-about-btn"><Button variant="outline">About VS-Solutions</Button></ExtLink>
            <ExtLink href={SITE.website} testid="founder-connect-btn"><Button className="bg-vs-royal hover:bg-vs-blue text-white">Connect with VS-Solutions<ArrowRight className="ml-2 h-4 w-4" /></Button></ExtLink>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------- Technology ---------------------------------- */
const TECH_GROUPS = [
  { icon: MonitorSmartphone, label: "Frontend", items: ["React", "TypeScript"] },
  { icon: Server, label: "Backend", items: ["Python", "FastAPI", "Java", "Spring Boot"] },
  { icon: Database, label: "Data", items: ["PostgreSQL", "MySQL", "Redis"] },
  { icon: Cloud, label: "Cloud", items: ["AWS", "Docker"] },
  { icon: KeyRound, label: "Security", items: ["JWT", "OAuth 2.0", "RBAC", "Audit Logging"] },
  { icon: Layers, label: "Integration", items: ["REST APIs", "WebSockets", "Payment APIs", "Email Infrastructure"] },
];

function Technology() {
  return (
    <section id="technology" className="py-14 sm:py-20 bg-slate-50 border-y border-slate-100" data-testid="technology-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead eyebrow="Engineering" title="Technology Behind the Ecosystem" desc="A modern, proven stack engineered for reliability, performance and scale." />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mt-10 sm:mt-12">
          {TECH_GROUPS.map((g) => (
            <div key={g.label} className="vs-card vs-card-hover p-4" data-testid={`tech-group-${g.label.toLowerCase()}`}>
              <div className="flex items-center gap-2 mb-3">
                <g.icon className="h-4 w-4 text-vs-royal" />
                <span className="text-xs font-bold uppercase tracking-widest text-vs-ink">{g.label}</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {g.items.map((t) => (
                  <span key={t} className="rounded-md bg-slate-50 border border-slate-200 px-2 py-1 text-[11px] font-semibold text-slate-700">{t}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ----------------------------------- Security ----------------------------------- */
const SECURITY = [
  "Role-Based Access Control", "Multi-Tenant Data Isolation", "Secure Authentication", "Protected APIs",
  "Audit Logging", "Activity History", "Document Controls", "Permission-Based Access",
  "Secure Business Communication", "Session Management",
];

function Security() {
  return (
    <section id="security" className="py-14 sm:py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="security-section">
      <div className="grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <SectionHead left eyebrow="Trust & Governance" title="Enterprise Security & Governance" desc="Every workspace, record and action is protected by layered access controls and complete traceability." />
          <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4 flex items-start gap-3">
            <ShieldCheck className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
            <p className="text-xs text-slate-500 leading-relaxed">
              Security capabilities listed here reflect controls implemented in the platform. We do not display certifications or compliance badges unless formally verified.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {SECURITY.map((s) => (
            <div key={s} className="flex items-center gap-2.5 rounded-lg border border-slate-200 bg-white px-3.5 py-3 hover:border-vs-royal transition-colors" data-testid={`security-item-${s.toLowerCase().replace(/[^a-z]/g, "-")}`}>
              <ShieldCheck className="h-4 w-4 text-emerald-600 shrink-0" />
              <span className="text-sm font-medium text-slate-800">{s}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------ Final CTA ------------------------------------ */
function FinalCTA() {
  return (
    <section className="py-14 sm:py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="final-cta-section">
      <div className="vs-gradient rounded-2xl p-8 sm:p-14 text-center relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-20" />
        <h2 className="relative font-display font-extrabold text-3xl sm:text-4xl text-white text-balance">Ready to Build Better Business Partnerships?</h2>
        <p className="relative mt-4 text-blue-100/90 text-sm sm:text-base max-w-xl mx-auto">
          Connect your business, partners and operations through one intelligent platform.
        </p>
        <div className="relative mt-8 flex flex-wrap justify-center gap-3.5">
          <Link to="/register"><Button size="lg" className="bg-white text-vs-deep hover:bg-blue-50 font-semibold" data-testid="cta-getstarted-btn">Get Started<ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
          <a href="#platform"><Button size="lg" className="bg-vs-royal text-white hover:bg-vs-blue font-semibold border border-white/20" data-testid="cta-explore-btn">Explore VS PartnerHub</Button></a>
          <ExtLink href={SITE.website} testid="cta-talk-btn"><Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 bg-transparent">Talk to VS-Solutions</Button></ExtLink>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------- Footer ------------------------------------- */
const FOOTER_COLS = [
  { title: "Platform", items: [
    { label: "What is PartnerHub", href: "#platform" }, { label: "Four Workspaces", href: "#workspaces" },
    { label: "How It Works", href: "#how-it-works" }, { label: "Capabilities", href: "#capabilities" },
    { label: "Security", href: "#security" },
  ]},
  { title: "Products", items: PRODUCTS.map((p) => ({ label: p.name, href: p.url, external: !!p.external, testid: `footer-${p.testid}` })) },
  { title: "VS-Solutions", items: [
    { label: "About Us", href: "#company" }, { label: "Founder & CEO", href: "#founder" },
    { label: "Technology", href: "#technology" }, { label: "Contact", href: "#contact" },
  ]},
  { title: "Support", items: [
    { label: "Help Center", href: "/contact", route: true }, { label: "Contact Support", href: "/contact", route: true },
    { label: "Login", href: "/login", route: true }, { label: "Get Started", href: "/register", route: true },
  ]},
];

function Footer() {
  return (
    <footer id="contact" className="bg-vs-navy text-slate-300" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 grid sm:grid-cols-2 lg:grid-cols-5 gap-10">
        <div>
          <Logo light />
          <p className="text-sm text-slate-400 mt-4 leading-relaxed">Connecting enterprises, trusted vendors and workforce partners through one intelligent platform.</p>
          <div className="mt-4 space-y-2 text-sm text-slate-400">
            <div className="flex items-center gap-2"><Globe className="h-4 w-4" /><ExtLink href={SITE.website} className="hover:text-white transition-colors" testid="footer-website">www.vs-solutions.in</ExtLink></div>
            <div className="flex items-center gap-2"><Mail className="h-4 w-4" />{SITE.contactEmail}</div>
          </div>
        </div>
        {FOOTER_COLS.map((col) => (
          <div key={col.title}>
            <div className="font-display font-semibold text-white text-sm mb-4">{col.title}</div>
            <ul className="space-y-2.5">
              {col.items.map((i) => (
                <li key={i.label}>
                  {i.external && i.href ? (
                    <ExtLink href={i.href} className="text-sm text-slate-400 hover:text-white transition-colors" testid={i.testid}>{i.label}</ExtLink>
                  ) : i.route ? (
                    <Link to={i.href} className="text-sm text-slate-400 hover:text-white transition-colors" data-testid={i.testid}>{i.label}</Link>
                  ) : (
                    <a href={i.href || "#"} className="text-sm text-slate-400 hover:text-white transition-colors" data-testid={i.testid}>{i.label}</a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row justify-between items-center gap-3 text-sm text-slate-400">
          <span>© {new Date().getFullYear()} Vijay Software Solutions Pvt Ltd</span>
          <span>Powered by Vijay Software Solutions Pvt Ltd</span>
        </div>
      </div>
    </footer>
  );
}

/* -------------------------------------- Page -------------------------------------- */
export default function Home() {
  useSEO({
    title: "VS PartnerHub | Enterprise Partner Management Platform | Vijay Software Solutions",
    path: "/",
  });
  return (
    <div className="bg-white">
      <Header />
      <Hero />
      <AtAGlance />
      <WhatIs />
      <Workspaces />
      <HowItWorks />
      <Capabilities />
      <Records360 />
      <Visibility />
      <Why />
      <Products />
      <Company />
      <Founder />
      <Technology />
      <Security />
      <FinalCTA />
      <Footer />
    </div>
  );
}
